import { AfterViewInit, ChangeDetectorRef, Component, OnChanges, OnInit, Input, Output, EventEmitter, SimpleChanges, ViewChild } from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import { ApexAxisChartSeries, ApexChart, ApexXAxis, ApexDataLabels, ApexTitleSubtitle, ApexResponsive, ApexTooltip, ApexPlotOptions, ApexLegend } from 'ngx-apexcharts';

@Component({
  selector: 'app-custom-apexchart',
  templateUrl: './custom-apexchart.component.html',
  styleUrls: ['./custom-apexchart.component.scss']
,
  standalone: false})
export class CustomApexchartComponent implements OnInit, AfterViewInit, OnChanges {
  public chartSeries: ApexAxisChartSeries[] = [];
  @Input() colors = [
    'rgba(255, 99, 132, 0.9)',// red
    'rgba(255, 159, 64, 0.9)',// orange
    '#ffcd56', // yellow
    '#9fbb58', // green 
    'rgba(54, 162, 235, 0.9)', //blue
    'rgba(75, 192, 192, 0.9)',
    'rgba(153, 102, 255, 0.98)'
  ];
  public labels = [];
  public chartName: string = 'chart';
  @Input() showGraph: boolean = false;
  @Input() chartHeight: string = '70vh';
  @Input() loading: boolean = false;
  @Input() customDownload: any = {};
  public isViewInitialized: boolean = false;

  public _chartOptions: any = undefined;
  @Input()
  set chartOptions(chartOptions: any) {
    this._chartOptions = chartOptions;
    this.chartDetails.toolbar.show = chartOptions.enableDownload || false;
    this.chartDetails.type = chartOptions.type || 'bar';
    this.chartDetails.stacked = chartOptions.stacked || true;
    this.chartDetails.toolbar.export.csv.filename = chartOptions.title;
    this.chartDetails.toolbar.export.svg.filename = chartOptions.title;
    this.chartDetails.toolbar.export.png.filename = chartOptions.title;
    this.title.text = chartOptions.title;
    this.chartName = chartOptions.uniqueName;
    this.plotOptions = chartOptions.plotOptions || {};
    this.legend = chartOptions.legend || {};
  }
  get chartOptions(): any {
    return this._chartOptions;
  }
  public _chartData: any = undefined;
  @Input()
  set chartData(chartData: any) {
    this._chartData = chartData;
    this.chartSeries = chartData.datasets;
    this.labels = chartData.labels;
    this.xaxis.categories = chartData.labels;
  }
  get chartData(): any {
    return this._chartData;
  }

  /**
   * Parent components currently control `showGraph`, but they often set it to `false`
   * even when they still pass an empty/zeroed `chartData` object.
   *
   * We want to render the chart whenever `chartData` is present, so the UI can show
   * an "empty" graph instead of a blank area.
   */
  public get hasChartData(): boolean {
    const cd = this._chartData;
    if (!cd) return false;
    return Array.isArray(cd.datasets) || Array.isArray(cd.labels);
  }

  /** True when any series has a non-zero numeric value (pie may use a raw number[] as `datasets`). */
  public get hasPopulatedData(): boolean {
    const cd = this._chartData;
    if (!cd || !Array.isArray(cd.datasets) || cd.datasets.length === 0) {
      return false;
    }
    const first = cd.datasets[0];
    if (typeof first === 'number') {
      return cd.datasets.some((v) => this.isNonZeroNumeric(v));
    }
    for (const ds of cd.datasets) {
      if (!ds) continue;
      const data = ds.data;
      if (Array.isArray(data)) {
        if (data.some((v) => this.isNonZeroNumeric(v))) return true;
      } else if (this.isNonZeroNumeric(data)) {
        return true;
      }
    }
    return false;
  }

  private isNonZeroNumeric(val: any): boolean {
    if (val == null || val === '') return false;
    const n = Number(val);
    return !isNaN(n) && n !== 0;
  }
  public makeSmallText(labels, maxLength = 30) {
    return labels.map(str => str.length > maxLength ? str.slice(0, maxLength) + '...' : str);
  }
  public chartDetails: ApexChart = {
    type: 'bar',
    stacked: true,
    height: 350,
    toolbar: {
      show: false,
      export: {
        csv: {
          filename: 'custom-filename-csv', // CSV export filename
        },
        svg: {
          filename: 'custom-filename-svg', // SVG export filename
        },
        png: {
          filename: 'custom-filename-png', // PNG export filename
        }
      }
    }
  };
  xaxis: ApexXAxis = {
    categories: [],
    labels: {
      trim: true,
      // formatter: (val: string): string => {
      //   // Truncate the label and add ellipsis
      //   return val.length > 10 ? val.substring(0, 10) + '...' : val;
      // }
    }
  };
  tooltip: ApexTooltip = {
    x: {
      formatter: (val, opts) => {
        return opts.w.globals.labels[opts.dataPointIndex]; // Show full label in tooltip
      }
    }
  };

  legend: ApexLegend = {}
  plotOptions: ApexPlotOptions = {}
  dataLabels: ApexDataLabels = {
    enabled: false
  };
  title: ApexTitleSubtitle = {
    text: 'Chart',
    align: 'center'
  };
  // Responsive settings
  responsive: ApexResponsive[] = [
    {
      breakpoint: 480,
      options: {
        chart: {
          width: 300
        },
        legend: {
          position: 'top'
        }
      }
    }
  ];
  @Output() downloadData = new EventEmitter<any>();
  // @ViewChild(BaseChartDirective) chartDirective!: BaseChartDirective;
  constructor(private cdr: ChangeDetectorRef) {
  }
  ngOnInit() { }
  
  ngAfterViewInit() {
    // Mark view as initialized after the view is ready
    // Use setTimeout to ensure DOM is fully rendered
    if (!this.loading) {
      setTimeout(() => {
        this.isViewInitialized = true;
        this.cdr.markForCheck();
      }, 0);
    }
  }
  
  ngOnChanges(changes: SimpleChanges) {
    // When loading changes from true to false, ensure view is initialized
    if (changes['loading'] && !changes['loading'].firstChange) {
      const currentLoading = changes['loading'].currentValue;
      const previousLoading = changes['loading'].previousValue;
      
      if (previousLoading === true && currentLoading === false) {
        // Loading just finished, ensure view is ready before rendering chart
        setTimeout(() => {
          this.isViewInitialized = true;
          this.cdr.markForCheck();
        }, 0);
      } else if (currentLoading === true) {
        // Loading started, reset view initialization
        this.isViewInitialized = false;
      }
    }
  }

  downloadImgMethod() {
    // const chart = this.chartDirective.chart; // Access the Chart.js instance
    // if (chart) {
    //   const link = document.createElement('a');
    //   link.href = chart.toBase64Image();
    //   link.download = this.chartName + '.png';
    //   link.click();
    // }
  }
  downloadDataMethod(type) {
    this.downloadData.emit({ type: type, chartName: this.chartName });
  }
}
